class PodcastSubscribe extends HTMLElement {
    constructor() {
        super();

        this.STORAGE_KEY = 'podcast_subscription_check';
        this.CHECK_DURATION = 4000;
        this.NAV_DELAY = 500;
        this.EXPIRY_TIME = 300000; // 5 minutes
        this.loyaltyApiUrl = '';

        this.platformActivityMap = {
            'APPLE': 'sub_applepod',
            'SPOTIFY': 'sub_spotify',
            'YOUTUBE': 'sub_youtube'
        };
    }

    connectedCallback() {
        this.loyaltyApiUrl = this.dataset.loyaltyRewardsUrl?.trim() ?? '';
        this.beforeUnloadHandler = () => this.persistDocumentHidden();
        window.addEventListener('beforeunload', this.beforeUnloadHandler);
        this.checkForPendingSubscription();
        this.attachClickHandlers();
        this.setupVisibilityListener();
    }

    disconnectedCallback() {
        this.cleanup();
    }

    setupVisibilityListener() {
        this.visibilityChangeHandler = () => {
            if (document.visibilityState === 'hidden') {
                this.persistDocumentHidden();
            } else if (document.visibilityState === 'visible') {
                this.checkForPendingSubscription();
            }
        };
        document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    }

    persistDocumentHidden() {
        try {
            const raw = sessionStorage.getItem(this.STORAGE_KEY);
            if (!raw) return;
            const parsed = JSON.parse(raw);
            if (!this.isActivePlatformFlow(parsed)) return;
            if (parsed.documentHidden) return;
            parsed.documentHidden = true;
            sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(parsed));
        } catch {
        }
    }

    attachClickHandlers() {
        this.querySelectorAll('.podcast-subscribe__platform-btn').forEach((button, index) => {
            if (button.href && button.href !== '#') {
                button.addEventListener('click', () => this.handlePlatformClick(button, index));
            }
        });
    }

    handlePlatformClick(button, index) {
        if (!this.getShopifyCustomerId()) {
            return;
        }

        const platform = String(button.dataset.platform || '').toLowerCase().trim();
        if (!this.isKnownPlatform(platform)) {
            return;
        }

        this.saveSubscriptionCheck({
            platformFlowStarted: true,
            platform,
            index,
            timestamp: Date.now(),
            hasNavigatedAway: false
        });

        setTimeout(() => this.markAsNavigatedAway(), this.NAV_DELAY);
    }

    isKnownPlatform(value) {
        const k = String(value || '').toLowerCase();
        return k === 'apple' || k === 'spotify' || k === 'youtube';
    }

    saveSubscriptionCheck(data) {
        try {
            sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
        } catch {
        }
    }

    markAsNavigatedAway() {
        try {
            const data = sessionStorage.getItem(this.STORAGE_KEY);
            if (data) {
                const parsed = JSON.parse(data);
                if (!this.isActivePlatformFlow(parsed)) {
                    return;
                }
                parsed.hasNavigatedAway = true;
                sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(parsed));
            }
            this.checkForPendingSubscription();
        } catch {
        }
    }

    isActivePlatformFlow(parsed) {
        if (!parsed || typeof parsed !== 'object') {
            return false;
        }
        if (parsed.platformFlowStarted !== true) {
            return false;
        }
        if (typeof parsed.index !== 'number' || parsed.index < 0) {
            return false;
        }
        if (!this.isKnownPlatform(parsed.platform)) {
            return false;
        }
        if (typeof parsed.timestamp !== 'number' || !Number.isFinite(parsed.timestamp)) {
            return false;
        }
        return true;
    }

    checkForPendingSubscription() {
        try {
            if (!this.getShopifyCustomerId()) {
                this.clearStorage();
                return;
            }

            const data = sessionStorage.getItem(this.STORAGE_KEY);
            if (!data) return;

            const parsed = JSON.parse(data);

            if (!this.isActivePlatformFlow(parsed)) {
                this.clearStorage();
                return;
            }

            if (!parsed.hasNavigatedAway) return;

            const timeSinceClick = Date.now() - parsed.timestamp;
            if (timeSinceClick >= this.EXPIRY_TIME) {
                this.clearStorage();
                return;
            }

            let navEntry = null;
            if (typeof performance !== 'undefined' && typeof performance.getEntriesByType === 'function') {
                navEntry = performance.getEntriesByType('navigation')[0] || null;
            }
            const allowWithoutHidden = navEntry && (navEntry.type === 'reload' || navEntry.type === 'back_forward');
            if (!parsed.documentHidden && !allowWithoutHidden) return;

            this.showCheckingState(parsed);
        } catch {
            this.clearStorage();
        }
    }

    showCheckingState(data) {
        if (this._checkingInProgress) return;

        const buttons = this.querySelectorAll('.podcast-subscribe__platform-btn');
        const targetButton = buttons[data.index];

        if (!targetButton) {
            this.clearStorage();
            return;
        }

        this._checkingInProgress = true;
        this.applyCheckingState(targetButton, data);
        this.startCountdown(targetButton, data);
    }

    applyCheckingState(button, data) {
        button.classList.add('podcast-subscribe__platform-btn--checking');
        button.style.pointerEvents = 'none';
        button.style.opacity = '0.8';

        const label = button.querySelector('.podcast-subscribe__platform-label');
        if (label) {
            label.dataset.originalText = label.textContent;
            label.innerHTML = `
                <span class="podcast-subscribe__checking-text">
                    <span class="podcast-subscribe__spinner"></span>
                    <span>Checking subscription...</span>
                </span>
            `;
        }
    }

    startCountdown(button, data) {
        let elapsed = 0;

        this.countdownInterval = setInterval(() => {
            elapsed += 100;

            if (elapsed >= this.CHECK_DURATION) {
                clearInterval(this.countdownInterval);
                this.resetButtonState(button);
                this.showConfirmationPopup(data);
            }
        }, 100);
    }

    showConfirmationPopup(data) {
        const platformInfo = this.getPlatformInfoFromKey(data.platform);
        const popup = this.createPopup(platformInfo);
        const previousFocus =
            document.activeElement instanceof HTMLElement ? document.activeElement : null;
        this._dialogReturnFocusEl = previousFocus;

        document.body.appendChild(popup);
        setTimeout(() => popup.classList.add('podcast-subscribe-popup--show'), 10);

        this.attachPopupHandlers(popup);
        requestAnimationFrame(() => {
            const firstBtn = popup.querySelector('.podcast-subscribe-popup__btn--yes');
            if (firstBtn && typeof firstBtn.focus === 'function') {
                firstBtn.focus();
            }
        });
    }

    getPlatformInfoFromKey(platformSlug) {
        const k = String(platformSlug || '').toLowerCase();
        if (k === 'apple') {
            return { name: 'Apple Podcasts', action: 'follow' };
        }
        if (k === 'spotify') {
            return { name: 'Spotify', action: 'follow' };
        }
        if (k === 'youtube') {
            return { name: 'YouTube', action: 'subscribe' };
        }
        return { name: 'this platform', action: 'follow' };
    }

    createPopup(platformInfo) {
        const popup = document.createElement('div');
        popup.className = 'podcast-subscribe-popup';
        const titleId = `podcast-subscribe-dialog-title-${Date.now().toString(36)}`;
        const actionText = platformInfo.action === 'subscribe' ? 'subscribe to' : 'follow';
        popup.setAttribute('role', 'dialog');
        popup.setAttribute('aria-modal', 'true');
        popup.setAttribute('aria-labelledby', titleId);

        popup.innerHTML = `
            <div class="podcast-subscribe-popup__overlay" aria-hidden="true"></div>
            <div class="podcast-subscribe-popup__content">
                <h3 id="${titleId}" class="podcast-subscribe-popup__title">Did you ${actionText} us on ${platformInfo.name}?</h3>
                <div class="podcast-subscribe-popup__actions">
                    <button type="button" class="podcast-subscribe-popup__btn podcast-subscribe-popup__btn--yes">Yes</button>
                    <button type="button" class="podcast-subscribe-popup__btn podcast-subscribe-popup__btn--no">No</button>
                </div>
            </div>
        `;
        return popup;
    }

    attachPopupHandlers(popup) {
        const returnFocusEl = this._dialogReturnFocusEl;

        const getFocusable = () => {
            const root = popup.querySelector('.podcast-subscribe-popup__content') || popup;
            const sel =
                'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
            return [...root.querySelectorAll(sel)].filter(
                (n) => n instanceof HTMLElement && n.offsetParent !== null
            );
        };

        const keydownHandler = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                closePopup();
                return;
            }
            if (e.key !== 'Tab') return;
            const nodes = getFocusable();
            if (nodes.length === 0) return;
            const first = nodes[0];
            const last = nodes[nodes.length - 1];
            const active = document.activeElement;
            if (e.shiftKey && active === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && active === last) {
                e.preventDefault();
                first.focus();
            }
        };

        popup.addEventListener('keydown', keydownHandler);

        const closePopup = () => {
            popup.removeEventListener('keydown', keydownHandler);
            popup.classList.remove('podcast-subscribe-popup--show');
            setTimeout(() => {
                popup.remove();
                this._checkingInProgress = false;
                this.clearStorage();
                this._dialogReturnFocusEl = null;
                if (returnFocusEl && typeof returnFocusEl.focus === 'function') {
                    try {
                        returnFocusEl.focus({ preventScroll: true });
                    } catch (_) {
                        returnFocusEl.focus();
                    }
                }
            }, 300);
        };

        const yesBtn = popup.querySelector('.podcast-subscribe-popup__btn--yes');
        const noBtn = popup.querySelector('.podcast-subscribe-popup__btn--no');

        if (yesBtn) {
            yesBtn.addEventListener('click', async (e) => {
                e.preventDefault();

                yesBtn.disabled = true;
                yesBtn.textContent = 'Processing...';

                const success = await this.handleSubscriptionConfirmation();

                closePopup();

                if (success) {
                    setTimeout(() => this.showRewardNotification(), 400);
                }
            });
        }

        if (noBtn) {
            noBtn.addEventListener('click', closePopup);
        }

        popup.querySelector('.podcast-subscribe-popup__overlay')?.addEventListener('click', closePopup);
    }

    resetButtonState(button) {
        button.classList.remove('podcast-subscribe__platform-btn--checking');
        button.style.pointerEvents = '';
        button.style.opacity = '';

        const label = button.querySelector('.podcast-subscribe__platform-label');
        if (label?.dataset.originalText) {
            label.textContent = label.dataset.originalText;
            delete label.dataset.originalText;
        }
    }

    async handleSubscriptionConfirmation() {
        try {
            const shopifyCustomerId = this.getShopifyCustomerId();
            if (!shopifyCustomerId) {
                return false;
            }

            const data = sessionStorage.getItem(this.STORAGE_KEY);

            if (!data) {
                return false;
            }

            const parsed = JSON.parse(data);

            const activityName = this.getActivityNameForPlatform(parsed.platform);

            if (!activityName) {
                return false;
            }

            if (!this.loyaltyApiUrl) {
                return false;
            }

            const response = await fetch(this.loyaltyApiUrl, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json'
                },
                body: JSON.stringify({
                    shopifyCustomerId,
                    activityName
                })
            });

            if (!response.ok) {
                await response.json().catch(() => null);
                return false;
            }

            const result = await response.json();

            return result.success;
        } catch {
            return false;
        }
    }

    getShopifyCustomerId() {
        const raw = this.dataset.customerId;
        if (!raw || raw === 'null' || raw === '') {
            return null;
        }
        const id = Number.parseInt(String(raw), 10);
        if (!Number.isInteger(id) || id <= 0) {
            return null;
        }
        return id;
    }

    getActivityNameForPlatform(platformSlug) {
        const key = String(platformSlug || '').toUpperCase();
        return this.platformActivityMap[key] ?? null;
    }

    showRewardNotification() {
        const notification = document.createElement('div');
        notification.className = 'podcast-reward-notification';
        notification.setAttribute('role', 'status');
        notification.setAttribute('aria-live', 'polite');
        notification.setAttribute('aria-atomic', 'true');
        notification.innerHTML = `
            <div class="podcast-reward-notification__content">
                <div class="podcast-reward-notification__icon" aria-hidden="true">🔓</div>
                <div class="podcast-reward-notification__text">
                    <h3 class="podcast-reward-notification__title">Reward Unlocked!</h3>
                    <p class="podcast-reward-notification__message">+300 points added to your account.</p>
                </div>
            </div>
        `;

        document.body.appendChild(notification);

        setTimeout(() => {
            notification.classList.add('podcast-reward-notification--show');
        }, 10);

        setTimeout(() => {
            notification.classList.remove('podcast-reward-notification--show');
            setTimeout(() => {
                notification.remove();
            }, 400);
        }, 4000);
    }

    clearStorage() {
        try {
            this._checkingInProgress = false;
            sessionStorage.removeItem(this.STORAGE_KEY);
        } catch {
        }
    }

    cleanup() {
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
        }
        if (this.visibilityChangeHandler) {
            document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
        }
        if (this.beforeUnloadHandler) {
            window.removeEventListener('beforeunload', this.beforeUnloadHandler);
        }
    }
}

if (customElements.get('podcast-subscribe') === undefined) {
    customElements.define('podcast-subscribe', PodcastSubscribe);
}
